import type { ProviderThread, ProviderThreadMessage } from "../providerMessagingTypes"

// One provider conversation as the panel sees it, with every wire-shape difference already resolved.
// The list, the header and the composer read this shape; only the normalizers below ever touch a wire
// shape.
//
// The Carrot normalizers that used to sit beside these are gone: the Carrot side of /messages is the
// app's own again (src/components/messaging/**) and reads its own payload directly.

export type UnifiedThread = {
  /** Healthie's conversation id, opaque to us. */
  threadId: string
  /** Healthie's own title, or null. Never a participant list, never the preview — see the wire type. */
  subject: string | null
  /** Epoch ms. `0` when the source gave us nothing usable, so the thread sorts LAST, not first. */
  updatedAt: number
  unread: boolean
  isClosed: boolean
  preview: string | null
  /** Whether the composer should be offered at all. */
  canReply: boolean
  /** Who the conversation is with, for its header. Null when nobody is resolved — see the wire type. */
  providerName: string | null
  /** Their picture, or null. Absent far more often than present; see the wire type. */
  providerAvatarUrl: string | null
  /**
   * Their credential — "MD" — or null. Kept APART from `providerName`; the label is composed at
   * the point of display and the header's initials read the bare name. See the wire type.
   */
  providerQualifications: string | null
}

export function fromProviderThread(thread: ProviderThread): UnifiedThread {
  return {
    threadId: thread.threadId,
    // Healthie's own title, or null when the conversation was never named — never a participant
    // list and never the preview; see the wire type. Carried through untouched: an untitled row
    // omits its subject LINE, which is the row's decision to make, not the normalizer's.
    subject: thread.subject,
    updatedAt: thread.updatedAt,
    unread: thread.unread,
    isClosed: thread.isClosed,
    preview: thread.preview,
    // There is no follow-up-thread mechanism on the provider side, so a closed conversation is
    // genuinely read-only — unlike a closed Carrot thread, whose composer stays up because replying
    // starts a follow-up. That asymmetry is why `canReply` exists rather than callers reading
    // `isClosed` themselves, and it is worth keeping now that only one side of it is left: the
    // components ask "may I reply?", which is the question they actually have.
    canReply: !thread.isClosed,
    // Carried through untouched, nulls included. Deciding what an absent name or an unusable picture
    // LOOKS like is the header's job, not the normalizer's — see `ProviderThreadHeader`.
    providerName: thread.providerName,
    providerAvatarUrl: thread.providerAvatarUrl,
    // Carried through SEPARATELY, never joined onto the name here. Composing the label is a
    // display decision (`providerLabel`), and the header's initials depend on this staying apart.
    //
    // `?? null` IS THE NORMALIZATION, not padding: the field is OPTIONAL on the wire so that a BFF
    // deployed ahead of the mirror degrades to "no credential" instead of failing the whole
    // response. That tolerance stops HERE — `UnifiedThread` keeps it `string | null`, so no
    // component has to distinguish "absent" from "none", and every `providerLabel` call site is
    // spared a third case.
    providerQualifications: thread.providerQualifications ?? null
  }
}

// One message in the open thread, normalized the same way.

export type UnifiedMessage = {
  /** React key / identity within one thread's rendered list. The provider gives a real message id. */
  id: string
  /** Runs through `getParsedHtmlElements`, like every message body this package renders. */
  body: string
  /** Epoch ms. */
  createdAt: number
  authoredByMember: boolean
  /** The author's display name, e.g. "Pat Smith". */
  authorLabel: string | null
}

export function fromProviderMessage(message: ProviderThreadMessage): UnifiedMessage {
  return {
    id: message.messageId,
    body: message.body,
    // Already epoch ms on the wire — the provider payload is normalized host-side.
    createdAt: message.createdAt,
    authoredByMember: message.authoredByMember,
    authorLabel: message.authorLabel
  }
}
